import React, { useState, useEffect } from 'react';
import MillosLogo from './MillosLogo';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  Send as SendIcon,
  Save as SaveIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import config from '../config';

interface Certificate {
  id: string;
  certificate_type: string;
  certificate_type_category?: string;
  property_address: string;
  inspector_name?: string;
  expiry_date: string;
}

interface EmailDraft {
  to: string;
  subject: string;
  body: string;
  tone: string;
  character_count: number;
  compressed: boolean;
}

interface GmailStatus {
  has_permissions: boolean;
  needs_auth: boolean;
  needs_reauth?: boolean;
  gmail_address?: string;
  error?: string;
}

interface EmailDialogProps {
  open: boolean;
  onClose: () => void;
  certificate: Certificate;
  onEmailSent?: (message: string) => void;
  onError?: (error: string) => void;
}

const EmailDialog: React.FC<EmailDialogProps> = ({
  open,
  onClose,
  certificate,
  onEmailSent,
  onError
}) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
  const [providers, setProviders] = useState<any[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>(''); // 'other' or provider.id
  const [customMessage, setCustomMessage] = useState('');
  const [emailDraft, setEmailDraft] = useState<EmailDraft | null>(null);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [gmailStatus, setGmailStatus] = useState<GmailStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setRecipientEmail('');
      setRecipientEmails([]);
      setSelectedProvider('');
      setCustomMessage('');
      setEmailDraft(null);
      setError(null);
      checkGmailStatus();
      fetchProviders();
    }
  }, [open, certificate]);

  const checkGmailStatus = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setError('No authentication token found. Please log in again.');
        setGmailStatus({
          has_permissions: false,
          needs_auth: true,
          error: 'Not authenticated'
        });
        return;
      }
      
      const response = await fetch(`${config.apiUrl}/api/gmail/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication expired. Please log in again.');
          setGmailStatus({
            has_permissions: false,
            needs_auth: true,
            error: 'Authentication required'
          });
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.gmail_status) {
        setGmailStatus(data.gmail_status);
      } else {
        setError(`Gmail status check failed: ${data.error || 'Unknown error'}`);
        setGmailStatus({
          has_permissions: false,
          needs_auth: true,
          error: data.error || 'Gmail status check failed'
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to check Gmail status: ${errorMessage}`);
      setGmailStatus({
        has_permissions: false,
        needs_auth: true,
        error: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch(`${config.apiUrl}/api/providers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        console.error('Failed to fetch providers');
        return;
      }

      const data = await response.json();
      if (data.success && data.providers) {
        // Use certificate_type_category for matching (e.g., "Licence Certificate") instead of certificate_type
        const certCategory = certificate.certificate_type_category || certificate.certificate_type || '';
        
        // Store ALL providers in state
        setProviders(data.providers);
        
        // Find matching providers for auto-selection
        const matchingProviders = data.providers.filter((provider: any) => {
          const certTypes = provider.certificate_types || [];
          // Match if provider handles this certificate type category
          return certTypes.some((type: string) => 
            type.toLowerCase().includes(certCategory.toLowerCase()) ||
            certCategory.toLowerCase().includes(type.toLowerCase())
          );
        });

        // Auto-select the first matching provider
        if (matchingProviders.length > 0) {
          setSelectedProvider(matchingProviders[0].id);
          setRecipientEmails([matchingProviders[0].provider_email]);
          setRecipientEmail(matchingProviders[0].provider_email);
        } else {
          setSelectedProvider('other');
        }
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
    }
  };

  const handleProviderChange = (event: any) => {
    const value = event.target.value;
    setSelectedProvider(value);
    
    if (value === 'other') {
      setRecipientEmails([]);
      setRecipientEmail('');
    } else {
      const provider = providers.find(p => p.id === value);
      if (provider) {
        setRecipientEmails([provider.provider_email]);
        setRecipientEmail(provider.provider_email);
      }
    }
  };

  const handleRemoveRecipient = (emailToRemove: string) => {
    setRecipientEmails(prev => prev.filter(email => email !== emailToRemove));
  };

  const handleAddRecipient = () => {
    if (recipientEmail && !recipientEmails.includes(recipientEmail)) {
      setRecipientEmails(prev => [...prev, recipientEmail]);
      setRecipientEmail('');
    }
  };

  const generateEmailDraft = async () => {
    if (recipientEmails.length === 0) {
      setError('Please add at least one recipient email address');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${config.apiUrl}/api/certificates/${certificate.id}/email-draft`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_email: recipientEmails.join(', '),
          custom_message: customMessage.trim()
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setEmailDraft(data.draft);
        setEditedSubject(data.draft.subject);
        setEditedBody(data.draft.body);
        setGmailStatus(data.gmail_status);
      } else {
        setError(`Failed to generate email: ${data.error}`);
      }
    } catch (err) {
      setError(`Network error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setGenerating(false);
    }
  };

  const saveEmailDraft = async () => {
    if (!emailDraft) return;

    try {
      setSaving(true);
      setError(null);
      
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${config.apiUrl}/api/certificates/${certificate.id}/email-save-draft`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: emailDraft.to,
          subject: editedSubject,
          body: editedBody
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        onEmailSent?.(`Draft saved to Gmail successfully`);
        onClose();
      } else {
        if (data.needs_auth || data.needs_reauth) {
          setError(`Gmail authentication required: ${data.error}. Please connect your Google account first.`);
        } else {
          setError(`Failed to save draft: ${data.error}`);
        }
      }
    } catch (err) {
      setError(`Network error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const sendEmail = async () => {
    if (!emailDraft) return;

    try {
      setSending(true);
      setError(null);
      
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${config.apiUrl}/api/certificates/${certificate.id}/email-send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: emailDraft.to,
          subject: editedSubject,
          body: editedBody
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        onEmailSent?.(`Email sent successfully to ${data.sent_to}`);
        onClose();
      } else {
        if (data.needs_auth || data.needs_reauth) {
          setError(`Gmail authentication required: ${data.error}. Please connect your Google account first.`);
        } else {
          setError(`Failed to send email: ${data.error}`);
        }
      }
    } catch (err) {
      setError(`Network error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSending(false);
    }
  };



  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon color="primary" />
          <Typography variant="h6">
            Email Inspection Request - {certificate.certificate_type}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Gmail Status Alert */}
            {gmailStatus && !gmailStatus.has_permissions && (
              <Alert 
                severity="warning" 
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('auth_token');
                        if (!token) {
                          setError('No authentication token found. Please log in again.');
                          return;
                        }
                        
                        // Make authenticated request to get OAuth URL
                        const response = await fetch(`${config.apiUrl}/api/auth/google/start`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            redirect_uri: `${window.location.origin}/auth/google/callback`
                          })
                        });
                        
                        if (!response.ok) {
                          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }
                        
                        const data = await response.json();
                        
                        if (data.success && data.auth_url) {
                          // Set up message listener for OAuth success
                          const handleOAuthMessage = (event: MessageEvent) => {
                            if (event.origin !== window.location.origin) return;
                            
                            if (event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
                              console.log('✅ OAuth success received in EmailDialog, refreshing status...');
                              // Refresh Gmail status immediately
                              setTimeout(() => checkGmailStatus(), 500);
                              window.removeEventListener('message', handleOAuthMessage);
                            }
                          };
                          
                          window.addEventListener('message', handleOAuthMessage);
                          
                          // Open OAuth URL in popup
                          const popup = window.open(data.auth_url, 'google-auth', 'width=500,height=600');
                          
                          // Check for popup closure and refresh status (fallback)
                          const checkClosed = setInterval(() => {
                            if (popup?.closed) {
                              clearInterval(checkClosed);
                              window.removeEventListener('message', handleOAuthMessage);
                              // Give a moment for the auth to complete, then recheck status
                              setTimeout(() => {
                                console.log('🔄 Popup closed, refreshing Gmail status...');
                                checkGmailStatus();
                              }, 1000);
                            }
                          }, 1000);
                        } else {
                          throw new Error(data.error || 'Failed to get OAuth URL');
                        }
                      } catch (err) {
                        setError(`Failed to start authentication: ${err instanceof Error ? err.message : 'Unknown error'}`);
                      }
                    }}
                  >
                    {gmailStatus.needs_reauth ? 'Re-authenticate Gmail' : 'Connect Gmail'}
                  </Button>
                }
              >
                {gmailStatus.needs_reauth 
                  ? '🔄 Gmail permissions needed: Your Google account is connected but missing email permissions. Please re-authenticate to grant Gmail access.'
                  : (typeof gmailStatus.error === 'string' ? gmailStatus.error : 'Gmail not connected. Please authenticate to send emails.')
                }
              </Alert>
            )}

            {gmailStatus?.has_permissions && (
              <Alert severity="success" 
                     action={
                       <Button 
                         color="inherit" 
                         size="small" 
                         onClick={() => {
                           console.log('🔄 Manual Gmail status refresh requested');
                           checkGmailStatus();
                         }}
                       >
                         Refresh
                       </Button>
                     }>
                Gmail connected: {gmailStatus.gmail_address || 'Unknown email'}
              </Alert>
            )}

            {/* Error Display */}
            {error && (
              <Alert severity="error">
                {typeof error === 'string' ? error : 'An error occurred'}
              </Alert>
            )}

            {/* Certificate Info */}
            <Box sx={{ p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Certificate Details
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {certificate.certificate_type}
              </Typography>
              <Typography variant="body2">
                <strong>Property:</strong> {certificate.property_address}
              </Typography>
              <Typography variant="body2">
                <strong>Inspector:</strong> {certificate.inspector_name || 'Not specified'}
              </Typography>
              <Typography variant="body2">
                <strong>Expires:</strong> {new Date(certificate.expiry_date).toLocaleDateString()}
              </Typography>
            </Box>

            {/* Email Form */}
            <Box display="flex" flexDirection="column" gap={2}>
              {/* Provider Selection Dropdown */}
              <FormControl fullWidth>
                <InputLabel>Recipient</InputLabel>
                <Select
                  value={selectedProvider}
                  label="Recipient"
                  onChange={handleProviderChange}
                >
                  {providers.map((provider: any) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.provider_name} ({provider.provider_email})
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem value="other">Other (Enter manually)</MenuItem>
                </Select>
              </FormControl>

              {/* Selected Recipients Display */}
              {recipientEmails.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.5 }}>
                    Recipients:
                  </Typography>
                  {recipientEmails.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      onDelete={() => handleRemoveRecipient(email)}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              )}
              
              {/* Manual Email Entry (only shown when "Other" is selected) */}
              {selectedProvider === 'other' && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Recipient Email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddRecipient();
                      }
                    }}
                    placeholder="contractor@example.com"
                    fullWidth
                    type="email"
                  />
                  <Button
                    onClick={handleAddRecipient}
                    variant="outlined"
                    disabled={!recipientEmail.trim()}
                    sx={{ minWidth: '100px' }}
                  >
                    Add
                  </Button>
                </Box>
              )}

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Optional: Customize Message</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    label="Custom Message"
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Customize your message with additional info like urgency, or additional details, the AI will include this in your response"
                    multiline
                    rows={4}
                    fullWidth
                  />
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* Generate Draft Button */}
            <Button
              onClick={generateEmailDraft}
              variant="contained"
              startIcon={generating ? <CircularProgress size={20} /> : <MillosLogo size={24} agent />}
              disabled={generating || recipientEmails.length === 0}
              fullWidth
              sx={{ mb: 2 }}
            >
              {generating ? 'Generating AI Draft...' : 'Generate AI Email Draft'}
            </Button>

            {/* Generated Email Draft */}
            {emailDraft && (
              <Box sx={{ 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1, 
                p: 2,
                bgcolor: 'background.paper'
              }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="subtitle1" color="primary">
                    Generated Email Draft
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Chip 
                      label={`Tone: ${emailDraft.tone}`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                    <Chip 
                      label={`${emailDraft.character_count} chars`} 
                      size="small" 
                      color={emailDraft.compressed ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    To: {emailDraft.to}
                  </Typography>
                  
                  <TextField
                    label="Subject"
                    value={editedSubject}
                    onChange={(e) => setEditedSubject(e.target.value)}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                  />
                </Box>

                <TextField
                  label="Email Body"
                  value={editedBody}
                  onChange={(e) => setEditedBody(e.target.value)}
                  multiline
                  rows={12}
                  fullWidth
                  variant="outlined"
                  helperText={`${editedBody.length} characters`}
                />

              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        
        <Button 
          onClick={generateEmailDraft}
          startIcon={<RefreshIcon />}
          disabled={generating || !recipientEmail.trim()}
          color="primary"
        >
          Regenerate
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {emailDraft && (
            <>
              <Button
                onClick={sendEmail}
                variant="contained"
                startIcon={
                  sending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SendIcon />
                  )
                }
                disabled={sending || saving || gmailStatus?.needs_auth === true}
                color="primary"
              >
                {sending ? 'Sending...' : 'Send Email'}
              </Button>

              <Button
                onClick={saveEmailDraft}
                variant="outlined"
                startIcon={
                  saving ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SaveIcon />
                  )
                }
                disabled={saving || sending || gmailStatus?.needs_auth === true}
                color="primary"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EmailDialog;